import React, { useEffect, useState } from 'react'
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import socket from '../controller/socket';
import { apiFetch } from '../utils/api';
import './DropdownNotification.css';

const formatRelativeTime = (value) => {
    if (!value) return '';
    const diffMs = Date.now() - new Date(value).getTime();
    const minutes = Math.max(Math.floor(diffMs / 60000), 0);
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h`;
    return `${Math.floor(hours / 24)} d`;
};

const severityIcon = {
    critical: 'material-symbols:error-outline',
    warning: 'material-symbols:pending-actions-outline',
    success: 'bitcoin-icons:verify-outline',
    info: 'iconoir:bell',
};

export default function DropdownNotification() {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingActionCount, setPendingActionCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const cargarNotificaciones = async () => {
        if (localStorage.getItem('isLoggedIn') !== 'true') return;
        setIsLoading(true);
        try {
            const data = await apiFetch('/api/cms-notifications?limit=8');
            if (data.status) {
                setItems(Array.isArray(data.data?.items) ? data.data.items : []);
                setUnreadCount(Number(data.data?.unreadCount) || 0);
                setPendingActionCount(Number(data.data?.pendingActionCount) || 0);
            }
        } catch (error) {
            console.error('Error cargando notificaciones CMS:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        cargarNotificaciones();
        socket.auth = { token: localStorage.getItem('auth_token') };
        if (!socket.connected) socket.connect();

        const refresh = () => cargarNotificaciones();
        socket.on('cms-notification:new', refresh);
        socket.on('cms-notification:updated', refresh);
        socket.on('cms-notification:refresh', refresh);

        return () => {
            socket.off('cms-notification:new', refresh);
            socket.off('cms-notification:updated', refresh);
            socket.off('cms-notification:refresh', refresh);
        };
    }, []);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleMouseEnter = () => {
        setIsDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        setIsDropdownOpen(false);
    };

    const abrirNotificacion = async (notification) => {
        try {
            await apiFetch(`/api/cms-notifications/${notification.id}/read`, { method: 'POST' });
        } catch (error) {
            console.error('Error marcando notificación:', error);
        }
        setIsDropdownOpen(false);
        await cargarNotificaciones();
        if (notification.targetPath) navigate(notification.targetPath);
    };

    const markAllRead = async () => {
        try {
            const data = await apiFetch('/api/cms-notifications/read-all', { method: 'POST' });
            if (data.status) {
                setItems(Array.isArray(data.data?.items) ? data.data.items : []);
                setUnreadCount(Number(data.data?.unreadCount) || 0);
                setPendingActionCount(Number(data.data?.pendingActionCount) || 0);
            }
        } catch (error) {
            console.error('Error marcando notificaciones:', error);
        }
    };

    const count = pendingActionCount || unreadCount;

    return (
        <div className="dropdown position-relative notification-dropdown" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
            className="w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center position-relative"
            type="button"
            onClick={toggleDropdown}
            >
            <Icon
                icon="iconoir:bell"
                className="text-primary-light text-xl"
            />
            {count > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger text-xs">
                    {count > 99 ? '99+' : count}
                </span>
            )}
            </button>
            <div className={`dropdown-menu dropdown-menu-lg p-0 notification-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}>
            <div className="notification-dropdown-header py-12 px-16 d-flex align-items-center justify-content-between gap-2">
                <div>
                <h6 className="text-lg text-primary-light fw-semibold mb-0">
                    Notificaciones
                </h6>
                </div>
                <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
                {count}
                </span>
            </div>
            <div className="notification-dropdown-list scroll-sm">
                {isLoading && items.length === 0 ? (
                    <div className="px-24 py-20 text-center text-secondary-light">Cargando...</div>
                ) : items.length === 0 ? (
                    <div className="px-24 py-20 text-center">
                        <Icon icon="iconoir:bell-off" className="text-xxl text-secondary-light mb-8" />
                        <p className="mb-0 text-sm text-secondary-light">No hay notificaciones pendientes.</p>
                    </div>
                ) : items.map((notification) => (
                    <button
                        type="button"
                        key={notification.id}
                        onClick={() => abrirNotificacion(notification)}
                        className={`notification-dropdown-item w-100 border-0 text-start px-24 py-12 d-flex align-items-start gap-3 justify-content-between ${notification.readAt ? 'bg-base' : 'bg-neutral-50'}`}
                    >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                            <span className={`w-44-px h-44-px rounded-circle d-flex justify-content-center align-items-center flex-shrink-0 ${notification.severity === 'warning' ? 'bg-warning-subtle text-warning-main' : notification.severity === 'critical' ? 'bg-danger-subtle text-danger-main' : 'bg-info-subtle text-info-main'}`}>
                                <Icon icon={severityIcon[notification.severity] || severityIcon.info} className="icon text-xxl" />
                            </span>
                            <div>
                                <h6 className="text-md fw-semibold mb-4">{notification.title}</h6>
                                <p className="mb-0 text-sm text-secondary-light text-w-200-px">{notification.body || 'Sin descripción'}</p>
                            </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                            {formatRelativeTime(notification.createdAt)}
                        </span>
                    </button>
                ))}
            </div>
            <div className="notification-dropdown-footer text-center py-12 px-16">
                <button
                type="button"
                onClick={markAllRead}
                className="border-0 bg-transparent text-primary-600 fw-semibold text-md"
                disabled={unreadCount === 0}
                >
                Marcar todas como leídas
                </button>
            </div>
            </div>
        </div>
    )
}
